import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/* Выбор даты в дизайне платформы — замена нативному <input type="date">.
   Значение снаружи всегда в формате YYYY-MM-DD, как у нативного инпута,
   поэтому подмена в формах ничего больше не требует. */

const MON = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const pad = (n) => String(n).padStart(2, '0');
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseIso = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || '');
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return isNaN(d) ? null : d;
};
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const IcCal = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IcChev = ({ dir }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points={dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
  </svg>
);

export default function DatePicker({ value, onChange, className = '', placeholder = 'Выберите дату', disabled }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const selected = parseIso(value);
  const [view, setView] = useState(() => selected || new Date());
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => { if (open) setView(parseIso(value) || new Date()); }, [open, value]);

  // Поповер живёт порталом в body — иначе его режет overflow у панели редактора
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (!r) return;
      const w = 292, h = 336;
      const left = Math.min(Math.max(8, r.left), window.innerWidth - w - 8);
      const below = r.bottom + 6;
      const top = below + h > window.innerHeight - 8 ? Math.max(8, r.top - h - 6) : below;
      setPos({ left, top, width: w });
    };
    const onScroll = (e) => { if (popRef.current?.contains(e.target)) return; place(); };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', onScroll, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Сетка месяца с понедельника, включая «хвосты» соседних месяцев
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const shift = (first.getDay() + 6) % 7;
  const start = new Date(first); start.setDate(1 - shift);
  const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const today = new Date();

  const label = selected
    ? selected.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' })
    : placeholder;

  const pick = (d) => { onChange(toIso(d)); setOpen(false); };

  return (
    <div ref={wrapRef} className="pk-wrap">
      <button type="button" disabled={disabled} className={`pk-field ${className}${open ? ' open' : ''}${selected ? '' : ' empty'}`}
        onClick={() => !disabled && setOpen(o => !o)}>
        <span className="pk-ic"><IcCal /></span>
        <span className="pk-val">{label}</span>
        {selected && !disabled && (
          <span className="pk-clear" role="button" title="Очистить"
            onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </span>
        )}
      </button>

      {open && pos && createPortal(
        <div ref={popRef} className="pk-pop" style={{ left: pos.left, top: pos.top, width: pos.width }}>
          <div className="pk-head">
            <button type="button" className="pk-nav" onClick={() => setView(v => new Date(v.getFullYear(), v.getMonth() - 1, 1))} title="Предыдущий месяц"><IcChev dir="left" /></button>
            <div className="pk-title">{MON[view.getMonth()]} {view.getFullYear()}</div>
            <button type="button" className="pk-nav" onClick={() => setView(v => new Date(v.getFullYear(), v.getMonth() + 1, 1))} title="Следующий месяц"><IcChev dir="right" /></button>
          </div>
          <div className="pk-wd">{WD.map(w => <span key={w}>{w}</span>)}</div>
          <div className="pk-grid">
            {days.map((d, i) => {
              const out = d.getMonth() !== view.getMonth();
              const cls = ['pk-day', out ? 'out' : '', sameDay(d, today) ? 'today' : '', sameDay(d, selected) ? 'sel' : ''].filter(Boolean).join(' ');
              return <button type="button" key={i} className={cls} onClick={() => pick(d)}>{d.getDate()}</button>;
            })}
          </div>
          <div className="pk-foot">
            <button type="button" className="pk-foot-btn" onClick={() => pick(new Date())}>Сегодня</button>
            <button type="button" className="pk-foot-btn ghost" onClick={() => { onChange(''); setOpen(false); }}>Очистить</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
